import React, { useState, useEffect, useCallback } from 'react';
import { BaseQuestion } from '@portal/components/questions/types';
import { QuestionFooter } from './QuestionHelpers';
import { tagsApi, TagSearchResult } from '@portal/api/tagsApi';
// import { useTeamContext } from '@portal/contexts/TeamContext'; // Not available in public forms
import { AsyncMultiSelect, Option } from './AsyncMultiSelect';
import { Plus } from 'lucide-react';

interface TagQuestionProps {
  question: BaseQuestion;
  value: TagQuestionValue;
  onChange: (value: TagQuestionValue) => void;
  onBlur: () => void;
  error?: string;
  primaryColor: string;
  moreInfo?: string;
  isMoreInfoExpanded?: boolean;
  onToggleMoreInfo?: () => void;
  availableTags?: Tag[];
  availableQualifications?: Qualification[];
  teamSlug?: string; // Optional team slug for API calls
}

export interface Tag {
  id: string;
  name: string;
  context?: string;
}

export interface TagQuestionValue {
  tagIds?: string[];
  tagRequests?: TagRequest[];
  questionResponseTaggings?: QuestionResponseTagging[];
}

export interface TagRequest {
  name: string;
  qualificationIds?: string[];
  qualificationOpenEndedResponseAttributes?: {
    response: string;
  };
}

export interface QuestionResponseTagging {
  id?: string;
  tagId: string;
  qualificationIds?: string[];
  qualificationOpenEndedResponseAttributes?: {
    response: string;
  };
}

export interface Qualification {
  id: string;
  name: string;
}

export const TagQuestion: React.FC<TagQuestionProps> = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  primaryColor,
  moreInfo,
  isMoreInfoExpanded,
  onToggleMoreInfo,
  availableTags = [],
  availableQualifications = [],
  teamSlug = '' // Will be provided by parent component
}) => {
  // const { teamSlug } = useTeamContext(); // Not available in public forms
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(value?.tagIds || []);
  const [tagRequests, setTagRequests] = useState<TagRequest[]>(value?.tagRequests || []);
  const [newTagName, setNewTagName] = useState('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  // Get tag question options from question
  const tagOptions = question.options || {};
  // If the question's answerStyle is 'tag_question', it should always be shown
  const showTagQuestion = question.answerStyle === 'tag_question' || tagOptions.show_tag_question === "1" || tagOptions.showTagQuestion;
  const seekingTags = tagOptions.seeking_tags === "1" || tagOptions.seekingTags;
  const requestTags = tagOptions.request_tags === "1" || tagOptions.requestTags;
  const qualificationsTitle = tagOptions.qualifications_title || tagOptions.qualificationsTitle || "Select Qualifications that apply:";
  const askOpenEndedQualification = tagOptions.ask_open_ended_qualification_question === "1" || tagOptions.askOpenEndedQualificationQuestion;
  const openEndedQuestion = tagOptions.open_ended_qualification_question || tagOptions.openEndedQualificationQuestion || "Provide details on how the tag applies to you";

  // Use availableQualifications prop if provided, otherwise fall back to question.qualifications
  const qualifications: Qualification[] = availableQualifications.length > 0 ? availableQualifications : (question.qualifications || []);

  // Debug logging
  console.log('TagQuestion render:', {
    questionId: question.id,
    answerStyle: question.answerStyle,
    showTagQuestion,
    options: tagOptions,
    availableTags,
    availableQualifications
  });

  // Search for tags via API
  const searchTags = useCallback(async (searchTerm: string): Promise<Option[]> => {
    // If no teamSlug provided, return empty results
    if (!teamSlug) {
      console.warn('TagQuestion: No teamSlug provided, tag search disabled');
      return [];
    }
    
    try {
      const results = await tagsApi.searchTags({
        teamSubdomain: teamSlug,
        query: searchTerm,
        context: seekingTags ? 'seeking' : 'providing'
      });
      
      return results.map(tag => ({
        value: tag.id,
        label: tag.name
      }));
    } catch (error) {
      console.error('Error searching tags:', error);
      return [];
    }
  }, [teamSlug, seekingTags]);

  // Build qualification responses from value prop - no need for state
  const qualificationResponses = React.useMemo(() => {
    const responses: Record<string, QuestionResponseTagging> = {};
    if (value?.questionResponseTaggings) {
      value.questionResponseTaggings.forEach(tagging => {
        responses[tagging.tagId] = tagging;
      });
    }
    // Ensure all selected tags have a response object
    selectedTagIds.forEach(tagId => {
      if (!responses[tagId]) {
        responses[tagId] = {
          tagId,
          qualificationIds: [],
          qualificationOpenEndedResponseAttributes: {
            response: ''
          }
        };
      }
    });
    return responses;
  }, [value?.questionResponseTaggings, selectedTagIds]);

  // Initialize selected tags when value changes
  useEffect(() => {
    if (value?.tagIds && value.tagIds.length > 0 && selectedTags.length === 0 && !requestTags) {
      // Load tag details for initial values
      searchTags('').then(allTags => {
        const tags = allTags
          .filter(opt => value.tagIds?.includes(opt.value))
          .map(opt => ({ id: opt.value, name: opt.label, context: seekingTags ? 'seeking' : 'providing' }));
        setSelectedTags(tags);
      }).catch(console.error);
    }
  }, [value?.tagIds, requestTags, seekingTags, searchTags, selectedTags.length]);

  const handleTagSelection = async (tagIds: string[]) => {
    setSelectedTagIds(tagIds);
    
    // Fetch tag details for newly selected tags
    if (tagIds.length > selectedTagIds.length) {
      const newTagIds = tagIds.filter(id => !selectedTagIds.includes(id));
      try {
        const allTags = await searchTags('');
        const newTags = allTags
          .filter(opt => newTagIds.includes(opt.value))
          .map(opt => ({ id: opt.value, name: opt.label, context: seekingTags ? 'seeking' : 'providing' }));
        setSelectedTags([...selectedTags, ...newTags]);
      } catch (error) {
        console.error('Error fetching tag details:', error);
      }
    } else {
      // Remove deselected tags
      setSelectedTags(selectedTags.filter(tag => tagIds.includes(tag.id)));
    }
    
    // Build new qualification responses preserving existing data
    const newQualificationResponses: QuestionResponseTagging[] = [];
    
    tagIds.forEach(tagId => {
      const existingResponse = value?.questionResponseTaggings?.find(qrt => qrt.tagId === tagId);
      if (existingResponse) {
        // Preserve existing qualification data
        newQualificationResponses.push(existingResponse);
      } else {
        // Create new response for newly selected tag
        newQualificationResponses.push({
          tagId,
          qualificationIds: [],
          qualificationOpenEndedResponseAttributes: {
            response: ''
          }
        });
      }
    });
    
    // Update the value
    const newValue: TagQuestionValue = {
      ...value,
      tagIds,
      questionResponseTaggings: newQualificationResponses
    };
    
    onChange(newValue);
    onBlur();
  };

  const handleAddTagRequest = () => {
    if (!newTagName.trim()) return;
    
    const newRequest: TagRequest = {
      name: newTagName.trim(),
      qualificationIds: [],
      qualificationOpenEndedResponseAttributes: {
        response: ''
      }
    };
    
    const updatedRequests = [...tagRequests, newRequest];
    setTagRequests(updatedRequests);
    setNewTagName('');
    
    onChange({
      ...value,
      tagRequests: updatedRequests
    });
  };

  const handleRemoveTagRequest = (index: number) => {
    const updatedRequests = tagRequests.filter((_, i) => i !== index);
    setTagRequests(updatedRequests);
    
    onChange({
      ...value,
      tagRequests: updatedRequests
    });
  };

  const handleQualificationChange = (tagIdOrIndex: string | number, qualificationIds: string[]) => {
    if (requestTags) {
      // Handle tag requests
      const index = tagIdOrIndex as number;
      const updatedRequests = [...tagRequests];
      updatedRequests[index] = {
        ...updatedRequests[index],
        qualificationIds
      };
      setTagRequests(updatedRequests);
      
      onChange({
        ...value,
        tagRequests: updatedRequests
      });
    } else {
      // Handle selected tags
      const tagId = tagIdOrIndex as string;
      
      // Create updated question response taggings
      const updatedTaggings = (value?.questionResponseTaggings || []).map(tagging => {
        if (tagging.tagId === tagId) {
          return {
            ...tagging,
            qualificationIds
          };
        }
        return tagging;
      });
      
      // If no existing tagging for this tag, create one
      if (!updatedTaggings.find(t => t.tagId === tagId)) {
        updatedTaggings.push({
          tagId,
          qualificationIds,
          qualificationOpenEndedResponseAttributes: {
            response: ''
          }
        });
      }
      
      onChange({
        ...value,
        questionResponseTaggings: updatedTaggings
      });
    }
  };

  const handleOpenEndedResponseChange = (tagIdOrIndex: string | number, response: string) => {
    if (requestTags) {
      // Handle tag requests
      const index = tagIdOrIndex as number;
      const updatedRequests = [...tagRequests];
      updatedRequests[index] = {
        ...updatedRequests[index],
        qualificationOpenEndedResponseAttributes: { response }
      };
      setTagRequests(updatedRequests);
      
      onChange({
        ...value,
        tagRequests: updatedRequests
      });
    } else {
      // Handle selected tags
      const tagId = tagIdOrIndex as string;
      
      // Create updated question response taggings
      const updatedTaggings = (value?.questionResponseTaggings || []).map(tagging => {
        if (tagging.tagId === tagId) {
          return {
            ...tagging,
            qualificationOpenEndedResponseAttributes: { response }
          };
        }
        return tagging;
      });
      
      // If no existing tagging for this tag, create one
      if (!updatedTaggings.find(t => t.tagId === tagId)) {
        updatedTaggings.push({
          tagId,
          qualificationIds: [],
          qualificationOpenEndedResponseAttributes: { response }
        });
      }
      
      onChange({
        ...value,
        questionResponseTaggings: updatedTaggings
      });
    }
  };

  if (!showTagQuestion) {
    return null;
  }

  const tagContextLabel = seekingTags ? "Seeking" : "Providing";

  return (
    <div className="space-y-4">
      <div className={error ? 'ring-2 ring-red-500 rounded-lg' : ''}>
        {requestTags ? (
          // Request Tags Mode
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTagRequest();
                  }
                }}
                placeholder={`Enter ${tagContextLabel.toLowerCase()} tag name`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddTagRequest}
                disabled={!newTagName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Tag
              </button>
            </div>
            
            {tagRequests.length > 0 && (
              <div className="space-y-2">
                {tagRequests.map((request, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <span className="flex-1">{request.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTagRequest(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Search Tags Mode
          <AsyncMultiSelect
            value={selectedTagIds}
            onChange={handleTagSelection}
            onSearch={searchTags}
            placeholder={`Search and select ${tagContextLabel.toLowerCase()} tags`}
            isMulti={true}
            id={question.id}
          />
        )}
      </div>

      {/* Qualifications Section */}
      {((requestTags && tagRequests.length > 0) || (!requestTags && selectedTagIds.length > 0)) && qualifications.length > 0 && (
        <div className="mt-6 space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">{qualificationsTitle}</h4>
          
          {requestTags ? (
            // Show qualifications for tag requests
            tagRequests.map((request, index) => (
              <div key={index} className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700">{request.name}</h5>
                <div className="space-y-2">
                  {qualifications.map(qual => (
                    <label key={qual.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={request.qualificationIds?.includes(qual.id) || false}
                        onChange={(e) => {
                          const currentIds = request.qualificationIds || [];
                          const newIds = e.target.checked
                            ? [...currentIds, qual.id]
                            : currentIds.filter(id => id !== qual.id);
                          handleQualificationChange(index, newIds);
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{qual.name}</span>
                    </label>
                  ))}
                </div>
                
                {askOpenEndedQualification && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {openEndedQuestion}
                    </label>
                    <textarea
                      value={request.qualificationOpenEndedResponseAttributes?.response || ''}
                      onChange={(e) => handleOpenEndedResponseChange(index, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            // Show qualifications for selected tags
            selectedTagIds.map(tagId => {
              const tag = selectedTags.find(t => t.id === tagId);
              const response = qualificationResponses[tagId];
              
              return (
                <div key={tagId} className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-700">{tag?.name}</h5>
                  <div className="space-y-2">
                    {qualifications.map(qual => (
                      <label key={qual.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={response?.qualificationIds?.includes(qual.id) || false}
                          onChange={(e) => {
                            const currentIds = response?.qualificationIds || [];
                            const newIds = e.target.checked
                              ? [...currentIds, qual.id]
                              : currentIds.filter(id => id !== qual.id);
                            handleQualificationChange(tagId, newIds);
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{qual.name}</span>
                      </label>
                    ))}
                  </div>
                  
                  {askOpenEndedQualification && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {openEndedQuestion}
                      </label>
                      <textarea
                        value={response?.qualificationOpenEndedResponseAttributes?.response || ''}
                        onChange={(e) => handleOpenEndedResponseChange(tagId, e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <QuestionFooter
        error={error}
        helpText={question.description}
        moreInfo={moreInfo}
        isMoreInfoExpanded={isMoreInfoExpanded}
        onToggleMoreInfo={onToggleMoreInfo}
      />
    </div>
  );
};